import type { AuditScore, Confidence, ScoreGrade } from '@/types/gmb';
import { CONFIDENCE_LABEL, gradeLabel, resolveGrade } from '@/constants/gmbMetrics';

const GRADE_STYLE: Record<ScoreGrade, string> = {
  A: 'bg-emerald-500 text-white',
  B: 'bg-lime-600 text-white',
  C: 'bg-amber-500 text-white',
  D: 'bg-orange-600 text-white',
  E: 'bg-rose-600 text-white',
};

const BAR_STYLE: Record<ScoreGrade, string> = {
  A: 'bg-emerald-500',
  B: 'bg-lime-600',
  C: 'bg-amber-500',
  D: 'bg-orange-600',
  E: 'bg-rose-600',
};

const CONFIDENCE_STYLE: Record<Confidence, string> = {
  High: 'bg-teal-50 text-teal-700 border-teal-200',
  Medium: 'bg-slate-100 text-slate-600 border-slate-200',
  Low: 'bg-amber-50 text-amber-700 border-amber-200',
};

export default function ScoreCard({ score }: { score: AuditScore }) {
  const maxScore = score.maxScore || 100;
  const total = score.total ?? 0;
  const grade = score.grade || resolveGrade(total);
  const percent = Math.max(0, Math.min(100, (total / maxScore) * 100));

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 pt-8 md:px-8 md:pt-10">
      <div className="grid grid-cols-1 gap-5 rounded-lg border border-slate-200 bg-white p-5 md:p-7 lg:grid-cols-3">
        <div className="flex items-center gap-5">
          <div className="flex flex-col items-center">
            <span className="text-5xl font-extrabold leading-none text-slate-900 md:text-6xl">{total}</span>
            <span className="mt-1 text-xs text-slate-400">/ {maxScore} 分</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`flex h-12 w-12 items-center justify-center rounded-lg text-xl font-bold ${GRADE_STYLE[grade]}`}>
              {grade}
            </span>
            <span className="text-sm font-medium text-slate-600">{gradeLabel(grade)}</span>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">整體健康度</p>
            <span className={`rounded-full border px-3 py-1 text-xs font-medium ${CONFIDENCE_STYLE[score.confidence]}`}>
              {CONFIDENCE_LABEL[score.confidence] || '可信度未知'}
            </span>
          </div>
          <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className={`h-full rounded-full ${BAR_STYLE[grade]}`} style={{ width: `${percent}%` }} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span>等級門檻：A ≥ 90、B ≥ 80、C ≥ 70、D ≥ 60</span>
            <span>滿分 {maxScore} 分</span>
          </div>
        </div>
      </div>
    </section>
  );
}